import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { ESIndex } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import CreatePipelineRunModal from './CreatePipelineRunModal';
import ViewPipelineRunsModal from './ViewPipelineRunsModal';
import ConfirmModal from '../ConfirmModal';
import '../shared.css';
import './ESIndexManager.css';

interface ESIndexManagerProps {
  kgId: string;
  versionId: string;
}

interface FieldInfo {
  name: string;
  type: string;
}

interface NodeFieldGroup {
  label: string;
  fields: FieldInfo[];
  docCount?: number;
}

interface IndexData extends ESIndex {
  nodeGroups: NodeFieldGroup[];
}

interface LabelCountsState {
  counts: { [label: string]: number };
  loading: boolean;
  error: string;
}

type SearchConfig = { [nodeLabel: string]: string[] };

interface IndexWarnings {
  missingNodes: string[];
  missingFromIndex: { [nodeLabel: string]: string[] };
  extraInIndex: { [nodeLabel: string]: string[] };
  countMismatches: { label: string; esCount: number; summaryCount: number }[];
}

const EXCLUDED_FIELDS = new Set(['neo4j_id', 'node_label']);

function parseMappings(mappings: any): NodeFieldGroup[] {
  const properties = mappings?.properties || {};
  const groupMap: { [label: string]: FieldInfo[] } = {};

  Object.keys(properties).forEach(fieldName => {
    if (EXCLUDED_FIELDS.has(fieldName)) return;

    const separatorIndex = fieldName.indexOf('__');
    if (separatorIndex === -1) return;

    const nodeLabel = fieldName.substring(0, separatorIndex);
    const propertyName = fieldName.substring(separatorIndex + 2);
    if (!propertyName) return;

    if (!groupMap[nodeLabel]) {
      groupMap[nodeLabel] = [];
    }

    const fieldType = properties[fieldName]?.type || 'text';
    groupMap[nodeLabel].push({ name: propertyName, type: fieldType });
  });

  return Object.keys(groupMap)
    .sort()
    .map(label => ({
      label,
      fields: groupMap[label].sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

function getSearchMismatches(
  nodeGroups: NodeFieldGroup[],
  searchConfig: SearchConfig | null,
): { missingFromIndex: { [nodeLabel: string]: string[] }; extraInIndex: { [nodeLabel: string]: string[] } } {
  if (!searchConfig) return { missingFromIndex: {}, extraInIndex: {} };

  const missingFromIndex: { [nodeLabel: string]: string[] } = {};
  const extraInIndex: { [nodeLabel: string]: string[] } = {};

  // Build ES field lookup by label (case-insensitive key → original label + field set)
  const esFieldsByLabelLower: { [labelLower: string]: Set<string> } = {};
  nodeGroups.forEach(group => {
    esFieldsByLabelLower[group.label.toLowerCase()] = new Set(
      group.fields.map(f => f.name.toLowerCase()),
    );
  });

  // Build _search lookup (case-insensitive key → property set)
  const searchByLabelLower: { [labelLower: string]: Set<string> } = {};
  Object.entries(searchConfig).forEach(([nodeLabel, props]) => {
    searchByLabelLower[nodeLabel.toLowerCase()] = new Set(props.map(p => p.toLowerCase()));
  });

  // _search → ES: properties expected by _search (+ name) but missing from index
  Object.entries(searchConfig).forEach(([nodeLabel, props]) => {
    const labelLower = nodeLabel.toLowerCase();
    const esFields = esFieldsByLabelLower[labelLower] || new Set<string>();
    const expected = [...props, 'name'];
    const missing = expected.filter(p => !esFields.has(p.toLowerCase()));
    if (missing.length > 0) missingFromIndex[nodeLabel] = missing;
  });

  // ES → _search: properties in index but not in _search (excluding name)
  nodeGroups.forEach(group => {
    const labelLower = group.label.toLowerCase();
    const searchProps = searchByLabelLower[labelLower] ?? new Set<string>();
    const extra = group.fields
      .filter(f => f.name.toLowerCase() !== 'name' && !searchProps.has(f.name.toLowerCase()))
      .map(f => f.name);
    if (extra.length > 0) extraInIndex[group.label] = extra;
  });

  return { missingFromIndex, extraInIndex };
}

const ESIndexManager: React.FC<ESIndexManagerProps> = ({ kgId, versionId }) => {
  const [indexes, setIndexes] = useState<IndexData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);
  const [labelCounts, setLabelCounts] = useState<{ [indexName: string]: LabelCountsState }>({});
  const [schemaNodes, setSchemaNodes] = useState<string[]>([]);
  const [summaryCounts, setSummaryCounts] = useState<{ [label: string]: number }>({});
  const [searchConfig, setSearchConfig] = useState<SearchConfig | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreatePipelineRun, setShowCreatePipelineRun] = useState(false);
  const [showViewRuns, setShowViewRuns] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const loadIndexes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.listESIndexes(kgId, versionId);
      if (response.data) {
        const aliasPrefix = `kg_${kgId}_${versionId}_`;
        const stripPrefix = (name: string) =>
          name.startsWith(aliasPrefix) ? name.slice(aliasPrefix.length) : name;
        const indexList: IndexData[] = response.data.map((item: any) => {
          const attrs = item.attributes || {};
          const rawAlias = attrs.alias_name || '';
          const rawAliasNames: string[] = attrs.alias_names || (rawAlias ? [rawAlias] : []);
          return {
            index_name: attrs.index_name || item.id,
            collection_name: attrs.collection_name || '',
            alias_name: rawAlias ? stripPrefix(rawAlias) : '',
            alias_names: rawAliasNames.map(stripPrefix),
            status: attrs.status || 'unknown',
            document_count: attrs.document_count ?? 0,
            size: attrs.size || '0b',
            nodeGroups: parseMappings(attrs.mappings),
          };
        });
        setIndexes(indexList);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load ES indexes'));
    } finally {
      setLoading(false);
    }
  }, [kgId, versionId]);

  const loadSchemaAndSummary = useCallback(async () => {
    try {
    const [schemaResult, summaryResult, customAttrsResult] = await Promise.allSettled([
      apiService.getSchema(kgId, versionId, false),
      apiService.getSummary(kgId, versionId, false),
      apiService.getCustomAttributes(kgId, versionId),
    ]);

    // Extract schema node labels
    if (schemaResult.status === 'fulfilled') {
      const response: any = schemaResult.value;
      const schemaData = response.data?.attributes?.schema
        || response.data?.schema
        || response.data?.attributes
        || response.data
        || {};

      let labels: string[] = [];
      if (schemaData.nodes && typeof schemaData.nodes === 'object') {
        labels = Object.keys(schemaData.nodes);
      } else if (schemaData.nodeLabels && Array.isArray(schemaData.nodeLabels)) {
        labels = schemaData.nodeLabels;
      } else if (schemaData.nodeProperties && typeof schemaData.nodeProperties === 'object') {
        labels = Object.keys(schemaData.nodeProperties);
      }
      setSchemaNodes(labels.map(l => l.toLowerCase()));
    }

    // Extract summary node counts
    if (summaryResult.status === 'fulfilled') {
      const response: any = summaryResult.value;
      const summaryData = response.data?.attributes?.summary
        || response.data?.summary
        || response.data?.attributes
        || response.data
        || {};

      const counts: { [label: string]: number } = {};

      if (summaryData.nodes) {
        Object.keys(summaryData.nodes).forEach(label => {
          const nodeData = summaryData.nodes[label];
          let count = 0;
          if (typeof nodeData === 'number') {
            count = nodeData;
          } else if (nodeData?.count !== undefined) {
            count = nodeData.count;
          } else if (nodeData?.node_count !== undefined) {
            count = nodeData.node_count;
          }
          counts[label.toLowerCase()] = count;
        });
      } else if (summaryData.node_counts) {
        Object.keys(summaryData.node_counts).forEach(label => {
          const count = summaryData.node_counts[label];
          counts[label.toLowerCase()] = typeof count === 'number' ? count : 0;
        });
      }
      setSummaryCounts(counts);
    }

    // Extract _search from custom attributes
    if (customAttrsResult.status === 'fulfilled') {
      const response: any = customAttrsResult.value;
      const attrs = response.data?.attributes?.custom_attributes
        || response.data?.attributes
        || {};
      const search = attrs._search;
      if (search && typeof search === 'object' && !Array.isArray(search)) {
        setSearchConfig(search as SearchConfig);
      } else {
        setSearchConfig(null);
      }
    }
    } catch {
      // schema/summary failures are non-fatal — index list still renders
    }
  }, [kgId, versionId]);

  const getMissingNodes = (nodeGroups: NodeFieldGroup[]): string[] => {
    if (schemaNodes.length === 0) return [];
    const esLabels = new Set(nodeGroups.map(g => g.label.toLowerCase()));
    return schemaNodes.filter(label => !esLabels.has(label));
  };

  const getCountMismatch = (label: string, esCount: number): { differs: boolean; summaryCount: number } | null => {
    const normalizedLabel = label.toLowerCase();
    if (summaryCounts[normalizedLabel] === undefined) return null;
    const summaryCount = summaryCounts[normalizedLabel];
    return { differs: summaryCount !== esCount, summaryCount };
  };

  const getIndexWarnings = (index: IndexData, countsState: LabelCountsState | undefined): IndexWarnings => {
    const missingNodes = getMissingNodes(index.nodeGroups);
    const { missingFromIndex, extraInIndex } = getSearchMismatches(index.nodeGroups, searchConfig);

    const countMismatches: { label: string; esCount: number; summaryCount: number }[] = [];
    if (countsState?.counts && Object.keys(countsState.counts).length > 0) {
      index.nodeGroups.forEach(group => {
        const docCount = countsState.counts[group.label.toLowerCase()];
        if (docCount !== undefined) {
          const mismatch = getCountMismatch(group.label, docCount);
          if (mismatch?.differs) {
            countMismatches.push({ label: group.label, esCount: docCount, summaryCount: mismatch.summaryCount });
          }
        }
      });
    }

    return { missingNodes, missingFromIndex, extraInIndex, countMismatches };
  };

  const hasWarnings = (w: IndexWarnings): boolean =>
    w.missingNodes.length > 0 ||
    Object.keys(w.missingFromIndex).length > 0 ||
    Object.keys(w.extraInIndex).length > 0 ||
    w.countMismatches.length > 0;

  const loadLabelCounts = useCallback(async (indexName: string) => {
    if (labelCounts[indexName]?.counts && Object.keys(labelCounts[indexName].counts).length > 0) return;

    setLabelCounts(prev => ({ ...prev, [indexName]: { counts: {}, loading: true, error: '' } }));

    try {
      const response: any = await apiService.queryESIndex(kgId, versionId, indexName, {
        size: 0,
        aggs: { label_counts: { terms: { field: 'node_label.keyword', size: 500 } } },
      });

      const counts: { [label: string]: number } = {};
      const buckets = response.data?.attributes?.aggregations?.label_counts?.buckets
        || response.aggregations?.label_counts?.buckets
        || [];
      buckets.forEach((bucket: any) => {
        counts[bucket.key.toLowerCase()] = bucket.doc_count;
      });

      setLabelCounts(prev => ({ ...prev, [indexName]: { counts, loading: false, error: '' } }));
    } catch (err: any) {
      setLabelCounts(prev => ({
        ...prev,
        [indexName]: { counts: {}, loading: false, error: extractErrorMessage(err, 'Failed to load document counts') },
      }));
    }
  }, [kgId, versionId, labelCounts]);

  const handleToggleExpand = useCallback((indexName: string) => {
    if (expandedIndex === indexName) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(indexName);
      loadLabelCounts(indexName);
    }
  }, [expandedIndex, loadLabelCounts]);

  const handleSetAsMain = useCallback((indexName: string) => {
    setConfirmAction({
      title: 'Set as Main Alias',
      message: `Set ES index "${indexName}" as the main alias?`,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiService.setESIndexAlias(kgId, versionId, indexName, 'main');
          await loadIndexes();
        } catch (err: any) {
          setError(extractErrorMessage(err, 'Failed to set main alias'));
        }
      },
    });
  }, [kgId, versionId, loadIndexes]);

  const handleDeleteIndex = useCallback((indexName: string) => {
    setConfirmAction({
      title: 'Delete ES Index',
      message: `Are you sure you want to delete ES index "${indexName}"? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiService.deleteESIndex(kgId, versionId, indexName);
          setIndexes(prev => prev.filter(idx => idx.index_name !== indexName));
          if (expandedIndex === indexName) setExpandedIndex(null);
        } catch (err: any) {
          setError(extractErrorMessage(err, 'Failed to delete ES index'));
        }
      },
    });
  }, [kgId, versionId, expandedIndex]);

  useEffect(() => {
    loadIndexes();
    loadSchemaAndSummary();
    apiService.getVersion(kgId, versionId).then((res: any) => {
      setOrgId(res?.data?.attributes?.org_id ?? '');
    }).catch(() => {
      // org_id is non-critical; silently ignore
    });
  }, [kgId, versionId, loadIndexes, loadSchemaAndSummary]);

  const indexNameKey = indexes.map(i => i.index_name).join(',');
  useEffect(() => {
    indexes.forEach(index => loadLabelCounts(index.index_name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexNameKey]);

  const filteredIndexes = indexes.filter((index) => {
    const searchLower = searchTerm.toLowerCase();
    return index.index_name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="es-index-manager tab-root">
      {/* Topbar */}
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <input
            type="text"
            className="tab-search"
            placeholder="Search indexes…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!loading && (
            <span className="tab-stat-pill">
              {filteredIndexes.length} of {indexes.length}
            </span>
          )}
        </div>
        <div className="tab-topbar-right">
          <button className="tab-btn tab-btn--ghost" onClick={() => setShowViewRuns(true)}>
            View Runs
          </button>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreatePipelineRun(true)}>
            + Pipeline Run
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      ) : indexes.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">No ES indexes yet</div>
          <div className="tab-empty-body">Create a pipeline run to build ES indexes for this version.</div>
        </div>
      ) : filteredIndexes.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⌕</div>
          <div className="tab-empty-title">No matches</div>
          <div className="tab-empty-body">No ES indexes match "{searchTerm}"</div>
        </div>
      ) : (
        <>
          <div className="es-indexes-table-container">
            <table className="es-indexes-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Index Name</th>
                  <th>Collection</th>
                  <th>Alias</th>
                  <th>Status</th>
                  <th>Doc Count</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIndexes.map((index) => {
                  const isExpanded = expandedIndex === index.index_name;
                  const countsState = labelCounts[index.index_name];
                  const warnings = getIndexWarnings(index, countsState);
                  const warnCount =
                    warnings.missingNodes.length +
                    Object.keys(warnings.missingFromIndex).length +
                    Object.keys(warnings.extraInIndex).length +
                    warnings.countMismatches.length;
                  return (
                    <React.Fragment key={index.index_name}>
                      <tr
                        className={`es-index-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => handleToggleExpand(index.index_name)}
                      >
                        <td className="expand-cell">
                          <button className="expand-toggle" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        </td>
                        <td>
                          <div className="index-name-cell">
                          <span className="index-name-text">{index.index_name}</span>
                          {hasWarnings(warnings) && (
                            <span className="index-warning-wrapper">
                              <span className="index-warning-icon">⚠</span>
                              <div className="index-warning-tooltip">
                                <div className="tooltip-title">{warnCount} warning{warnCount !== 1 ? 's' : ''}</div>
                                {warnings.missingNodes.length > 0 && (
                                  <div className="tooltip-section">
                                    <span className="tooltip-label">Schema nodes missing from index:</span>
                                    <span className="tooltip-items">{warnings.missingNodes.join(', ')}</span>
                                  </div>
                                )}
                                {Object.entries(warnings.missingFromIndex).map(([node, props]) => (
                                  <div key={node} className="tooltip-section">
                                    <span className="tooltip-label">Missing from index ({node}):</span>
                                    <span className="tooltip-items">{props.join(', ')}</span>
                                  </div>
                                ))}
                                {Object.entries(warnings.extraInIndex).map(([node, props]) => (
                                  <div key={node} className="tooltip-section">
                                    <span className="tooltip-label">Extra in index ({node}):</span>
                                    <span className="tooltip-items">{props.join(', ')}</span>
                                  </div>
                                ))}
                                {warnings.countMismatches.map(m => (
                                  <div key={m.label} className="tooltip-section">
                                    <span className="tooltip-label">Doc count mismatch ({m.label}):</span>
                                    <span className="tooltip-items">ES: {m.esCount.toLocaleString()} vs summary: {m.summaryCount.toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </span>
                          )}
                          </div>
                        </td>
                        <td>{index.collection_name}</td>
                        <td>{index.alias_name}</td>
                        <td>{index.status}</td>
                        <td>{index.document_count.toLocaleString()}</td>
                        <td>{index.size}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="tab-btn tab-btn--ghost"
                              style={{ visibility: (index.alias_names ?? []).includes('main') ? 'hidden' : 'visible' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetAsMain(index.index_name);
                              }}
                              title="Set as main alias"
                            >
                              Set as Main
                            </button>
                            <button
                              className="tab-btn tab-btn--danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteIndex(index.index_name);
                              }}
                              title="Delete ES index"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="es-index-detail-row">
                          <td colSpan={8}>
                            <div className="es-index-details">
                              {index.nodeGroups.length === 0 ? (
                                <div className="empty-state">No fields found in this index.</div>
                              ) : (
                                <div className="es-schema-content">
                                  {warnings.missingNodes.length > 0 && (
                                    <div className="es-warning-banner">
                                      <span className="warning-icon">⚠</span>
                                      <span>Schema nodes missing from ES index:</span>
                                      <div className="es-missing-node-tags">
                                        {warnings.missingNodes.map(label => (
                                          <span key={label} className="es-missing-node-tag">{label}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {searchConfig !== null && Object.keys(warnings.missingFromIndex).length > 0 && (
                                    <div className="es-warning-banner">
                                      <span className="warning-icon">⚠</span>
                                      <div className="es-warning-banner-body">
                                        <span>Properties in <code>_search</code> missing from index:</span>
                                        {Object.entries(warnings.missingFromIndex).map(([node, props]) => (
                                          <div key={node} className="es-warning-node-row">
                                            <span className="es-missing-node-tag">{node}</span>
                                            <div className="es-missing-prop-tags">
                                              {props.map(p => (
                                                <span key={p} className="es-missing-prop-tag">{p}</span>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {searchConfig !== null && Object.keys(warnings.extraInIndex).length > 0 && (
                                    <div className="es-warning-banner es-warning-banner--info">
                                      <span className="warning-icon">⚠</span>
                                      <div className="es-warning-banner-body">
                                        <span>Properties in index not present in <code>_search</code>:</span>
                                        {Object.entries(warnings.extraInIndex).map(([node, props]) => (
                                          <div key={node} className="es-warning-node-row">
                                            <span className="es-missing-node-tag">{node}</span>
                                            <div className="es-missing-prop-tags">
                                              {props.map(p => (
                                                <span key={p} className="es-missing-prop-tag">{p}</span>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {countsState?.loading && (
                                    <div className="page-loader" style={{ minHeight: 32 }}><div className="page-spinner" /></div>
                                  )}
                                  {countsState?.error && (
                                    <div className="error-message">{countsState.error}</div>
                                  )}
                                  {index.nodeGroups.map((group) => {
                                    const docCount = countsState?.counts?.[group.label.toLowerCase()];
                                    const mismatch = docCount !== undefined ? getCountMismatch(group.label, docCount) : null;
                                    return (
                                      <div key={group.label} className="schema-group">
                                        <div className="schema-group-header">
                                          <div className="header-content">
                                            <h3>
                                              <span className="node-badge">NODE</span>
                                              {group.label}
                                              <span className="property-count">({group.fields.length} properties)</span>
                                              {docCount !== undefined && (
                                                <span className={`entity-count${mismatch?.differs ? ' count-mismatch' : ''}`}>
                                                  {docCount.toLocaleString()} docs
                                                </span>
                                              )}
                                              {mismatch?.differs && (
                                                <span className="mismatch-indicator">
                                                  ⚠ (summary: {mismatch.summaryCount.toLocaleString()})
                                                </span>
                                              )}
                                            </h3>
                                          </div>
                                        </div>
                                        <div className="schema-table-container">
                                          <table className="schema-table">
                                            <thead>
                                              <tr>
                                                <th>Property</th>
                                                <th>Type</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {group.fields.map((field) => (
                                                <tr key={field.name}>
                                                  <td className="property-name">{field.name}</td>
                                                  <td>
                                                    <span className={`type-badge type-${field.type.toLowerCase()}`}>
                                                      {field.type}
                                                    </span>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showCreatePipelineRun && (
        <CreatePipelineRunModal
          kgId={kgId}
          versionId={versionId}
          orgId={orgId}
          onClose={() => { setShowCreatePipelineRun(false); loadIndexes(); }}
        />
      )}

      {showViewRuns && (
        <ViewPipelineRunsModal
          kgId={kgId}
          versionId={versionId}
          orgId={orgId}
          onClose={() => setShowViewRuns(false)}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.title.startsWith('Delete') ? 'Delete' : 'Confirm'}
          danger={confirmAction.title.startsWith('Delete')}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

export default ESIndexManager;
